import { TFileSchema } from '@conversed/shared/src/central/types/common.type';
import { z } from 'zod';

/**
 * 🔄 Repetitive Mistake – AI keeps making the same error.
 * 🎯 Off-Topic – AI is responding to something unrelated.
 * 🤖 Hallucination – AI is generating incorrect/made-up information.
 * 🚫 Compliance Issue – AI is sharing misleading or incorrect information.
 * 🤷 I don’t know – Users are unsure about the issue
 */
export enum ReportIssueCategory {
  REPETITIVE_MISTAKE = 'repetitive_mistake',
  OFF_TOPIC = 'off_topic',
  HALLUCINATION = 'hallucination',
  COMPLIANCE_ISSUE = 'compliance_issue',
  I_DONT_KNOW = 'i_dont_know',
}

/**
 * 🟢 Minor Issue – Low priority, just something to check.
 * 🟠 Needs Attention (Default) – Could cause confusion.
 * 🔴 Urgent Fix – Critical, must be addressed immediately.
 */
export enum ReportIssuePriority {
  MINOR_ISSUE = 'minor_issue',
  NEEDS_ATTENTION = 'needs_attention',
  URGENT_FIX = 'urgent_fix',
}

export const ReportIssueAttachmentSchema = z.object({
  fileName: z.string(),
  file: z.union([TFileSchema, z.string()]),
});

export const ReportIssueParamsSchema = z.object({
  categories: z
    .enum(
      Object.values(ReportIssueCategory) as [
        ReportIssueCategory,
        ...ReportIssueCategory[],
      ],
    )
    .array(),
  priority: z.enum(
    Object.values(ReportIssuePriority) as [
      ReportIssuePriority,
      ...ReportIssuePriority[],
    ],
  ),
  comment: z.string().optional(),
  attachments: ReportIssueAttachmentSchema.array().optional(),
});

export type TReportIssueParams = z.infer<typeof ReportIssueParamsSchema>;
export type TReportIssueAttachments = z.infer<
  typeof ReportIssueAttachmentSchema
>;

export const reportIssueCategoryMapping: {
  key: ReportIssueCategory;
  labelTranslationKey: string;
  icon: string;
}[] = [
  {
    key: ReportIssueCategory.REPETITIVE_MISTAKE,
    labelTranslationKey:
      'train-ai.fact-checking.report-issue.category.repetitive_mistake',
    icon: 'counter-clock-wise-arrows.svg',
  },
  {
    key: ReportIssueCategory.OFF_TOPIC,
    labelTranslationKey:
      'train-ai.fact-checking.report-issue.category.off_topic',
    icon: 'direct-hit.svg',
  },
  {
    key: ReportIssueCategory.HALLUCINATION,
    labelTranslationKey:
      'train-ai.fact-checking.report-issue.category.hallucination',
    icon: 'robot.svg',
  },
  {
    key: ReportIssueCategory.COMPLIANCE_ISSUE,
    labelTranslationKey:
      'train-ai.fact-checking.report-issue.category.compliance_issue',
    icon: 'prohibited.svg',
  },
  {
    key: ReportIssueCategory.I_DONT_KNOW,
    labelTranslationKey:
      'train-ai.fact-checking.report-issue.category.i_dont_know',
    icon: 'person-shrugging.svg',
  },
];

export const reportIssuePriorityMapping: {
  key: ReportIssuePriority;
  labelTranslationKey: string;
  icon: string;
}[] = [
  {
    key: ReportIssuePriority.MINOR_ISSUE,
    labelTranslationKey:
      'train-ai.fact-checking.report-issue.priority.minor_issue',
    icon: 'green-circle.svg',
  },
  {
    key: ReportIssuePriority.NEEDS_ATTENTION,
    labelTranslationKey:
      'train-ai.fact-checking.report-issue.priority.needs_attention',
    icon: 'orange-circle.svg',
  },
  {
    key: ReportIssuePriority.URGENT_FIX,
    labelTranslationKey:
      'train-ai.fact-checking.report-issue.priority.urgent_fix',
    icon: 'red-circle.svg',
  },
];
